'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Reminder } from '@/core/domain';
import { handleError } from '@/core/errors/handle-error';
import type { GetUserUidUseCase } from '@/features/authentication/domain/usecases/get-user-uid';
import type { GetAllRemindersUseCase } from '../../domain/usecases/get-all-reminders';
import type { UpdateReminderUseCase } from '../../domain/usecases/update-reminder';

export interface ReminderState {
  reminders: Reminder[];
  loading: boolean;
  error: string | null;
}

export interface ReminderOperationResult {
  success: boolean;
  error: string | null;
}

export interface ReminderEvents {
  /** Carrega a lista de lembretes do usuário. `forceRefresh` ignora cache local primeiro. */
  getByUserId: (options?: { forceRefresh?: boolean }) => Promise<Reminder[]>;
  /** Atualiza um lembrete (ex.: contato/agendamento). Em sucesso, recarrega a lista. */
  update: (id: string, partial: Partial<Reminder>) => Promise<ReminderOperationResult>;
  clearError: () => void;
}

export interface ReminderContextValue {
  state: ReminderState;
  events: ReminderEvents;
}

export interface ReminderProviderProps {
  children: React.ReactNode;
  getUserUidUseCase: GetUserUidUseCase;
  getAllRemindersUseCase: GetAllRemindersUseCase;
  updateReminderUseCase: UpdateReminderUseCase;
}

const initialState: ReminderState = {
  reminders: [],
  loading: false,
  error: null,
};

const CTX_LOG = '[Reminders][Context]';

const ctxDebug = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(CTX_LOG, ...args);
  }
};

export const ReminderContext = createContext<ReminderContextValue | null>(null);

export function ReminderProvider({
  children,
  getUserUidUseCase,
  getAllRemindersUseCase,
  updateReminderUseCase,
}: ReminderProviderProps) {
  const [state, setState] = useState<ReminderState>(initialState);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const getUserUid = useCallback(async (): Promise<string | null> => {
    const userId = await getUserUidUseCase.execute();
    if (!userId) {
      setState((s) => ({ ...s, error: 'Usuário não autenticado.', reminders: [] }));
      return null;
    }
    return userId;
  }, [getUserUidUseCase]);

  const getByUserId = useCallback(
    async (options?: { forceRefresh?: boolean }): Promise<Reminder[]> => {
      const userId = await getUserUid();
      if (!userId) {
        ctxDebug('getByUserId: no uid, skip');
        return [];
      }
      ctxDebug('getByUserId: start', { userId, forceRefresh: options?.forceRefresh === true });
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const list = await getAllRemindersUseCase.execute(userId, options);
        ctxDebug('getByUserId: success', { userId, count: list.length, ids: list.map((r) => r.id) });
        setState((s) => ({ ...s, reminders: list }));
        return list;
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        // eslint-disable-next-line no-console
        console.error(CTX_LOG, 'getByUserId: error', {
          userId,
          forceRefresh: options?.forceRefresh === true,
          errorMessage,
          err: e,
        });
        setState((s) => ({ ...s, error: errorMessage, reminders: [] }));
        return [];
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [getUserUid, getAllRemindersUseCase]
  );

  const update = useCallback(
    async (id: string, partial: Partial<Reminder>): Promise<ReminderOperationResult> => {
      const userId = await getUserUid();
      if (!userId) {
        const err = 'Usuário não autenticado.';
        setState((s) => ({ ...s, error: err }));
        return { success: false, error: err };
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await updateReminderUseCase.execute(id, {
          ...partial,
          updated_at: new Date().toISOString(),
        });
        await getByUserId({ forceRefresh: true });
        return { success: true, error: null };
      } catch (e) {
        const f = handleError(e);
        setState((s) => ({ ...s, error: f.message }));
        return { success: false, error: f.message };
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [getUserUid, updateReminderUseCase, getByUserId]
  );

  const events: ReminderEvents = useMemo(
    () => ({
      getByUserId,
      update,
      clearError,
    }),
    [getByUserId, update, clearError]
  );

  const value: ReminderContextValue = useMemo(
    () => ({ state, events }),
    [state, events]
  );

  return (
    <ReminderContext.Provider value={value}>
      {children}
    </ReminderContext.Provider>
  );
}

export function useReminders(): ReminderContextValue {
  const ctx = useContext(ReminderContext);
  if (!ctx) {
    throw new Error('useReminders must be used within ReminderProvider');
  }
  return ctx;
}
